import type { RelationOutputForBuyer } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useGetRelations } from "@/services";
import { currentUserAtom } from "@/store/login";
import { formatNumber } from "@/utils/tools";
import {
	Chip,
	Skeleton,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Typography,
} from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import type { FC } from "react";
import {
	DashboardTable,
	EmptyRow,
	LoadingRows,
	SectionCard,
} from "./DashboardPrimitives";

const TOP_COUNT = 5;

/** What your buyers owe you in total, and who owes the most. */
export const DashboardTopBuyers: FC<{
	debtsToReceive: number;
	loading?: boolean;
}> = ({ debtsToReceive, loading }) => {
	const { t } = useTranslation();
	const user = useAtomValue(currentUserAtom);

	// ponytail: the API has no debt ordering, so rank one page client-side.
	// Swap for a server-side `ordering=-current_debt` when it exists.
	const { data, isLoading } = useGetRelations({
		query: {
			page: 1,
			page_size: 50,
			seller_id: user?.id,
			statuses: ["ACCEPTED"],
		},
	});

	const buyers = ((data?.results ?? []) as RelationOutputForBuyer[])
		.filter((relation) => Number(relation.current_debt) > 0)
		.sort((a, b) => Number(b.current_debt) - Number(a.current_debt))
		.slice(0, TOP_COUNT);

	return (
		<SectionCard
			title={t("dashboard.menuBuyerRelations")}
			viewAllTo="/relations/buyer"
			metric={
				loading ? (
					<Skeleton className="mt-1 h-6 w-32" />
				) : (
					<span className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
						<Typography variant="title-md" as="span">
							{formatNumber(debtsToReceive, "price")}
						</Typography>
						<Typography
							variant="body-sm"
							as="span"
							className="text-secondary-text-100"
						>
							{t("home.deptsToReceive")}
						</Typography>
					</span>
				)
			}
		>
			<DashboardTable compact>
				<TableHeader>
					<TableRow>
						<TableHead>{t("relation.tableBuyerName")}</TableHead>
						<TableHead>{t("relation.tableDebt")}</TableHead>
						<TableHead>{t("relation.tableMaximumDebt")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{isLoading ? (
						<LoadingRows columns={3} />
					) : buyers.length === 0 ? (
						<EmptyRow colSpan={3}>{t("home.noBuyerDebts")}</EmptyRow>
					) : (
						buyers.map((buyer) => {
							const debt = Number(buyer.current_debt);
							const maxDebt = Number(buyer.max_debt);
							const atLimit = maxDebt > 0 && debt >= maxDebt;

							return (
								<TableRow key={buyer.id}>
									<TableCell data-label={t("relation.tableBuyerName")}>
										<RelationNameLink
											name={buyer.buyer_display_name}
											relationId={buyer.id}
											party="buyer"
										/>
									</TableCell>
									<TableCell data-label={t("relation.tableDebt")}>
										<span className="flex flex-wrap items-center gap-2">
											{formatNumber(debt, "price")}
											{atLimit ? (
												<Chip variant="warning" size="sm">
													{t("home.atDebtLimit")}
												</Chip>
											) : null}
										</span>
									</TableCell>
									<TableCell data-label={t("relation.tableMaximumDebt")}>
										{formatNumber(maxDebt, "price")}
									</TableCell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</DashboardTable>
		</SectionCard>
	);
};
